<script lang="ts" setup>
  import { ref, computed } from 'vue';

  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';
  import {
    MpfAttachmentsProps,
    mpfAttachmentsDefaults,
    MpfAttachmentsEmits,
  } from './MpfAttachments.props';
  import './MpfAttactments.scss';

  const props = withDefaults(defineProps<MpfAttachmentsProps>(), mpfAttachmentsDefaults);
  const emit = defineEmits<MpfAttachmentsEmits>();

  defineOptions({ inheritAttrs: false });

  const files = ref<File[]>([]);
  const fileErrors = ref<string | null>(null);

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);

  const groupClasses = computed(() => [
    'mpf-form-group',
    hasErrors.value || fileErrors.value ? 'mpf-form-group--error' : '',
  ]);

  const hintId = computed(() => (props.hint ? `${props.id}-hint` : undefined));
  const errorId = computed(() => (hasErrors.value ? `${props.id}-error` : undefined));

  function onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    fileErrors.value = null;

    const incoming = Array.from(input.files);

    const validFiles: File[] = [];
    for (const file of incoming) {
      if (file.size > props.maxFileSize) {
        fileErrors.value = `${file.name} exceeds the maximum file size of 2MB.`;
        continue;
      }
      validFiles.push(file);
    }

    const nextFiles = [...files.value, ...validFiles].slice(0, props.maxFiles);

    files.value = nextFiles;
    emit('update:modelValue', nextFiles);
    input.value = '';
  }
</script>

<template>
  <!--  TODO, notification / banner-->
  <!--  <div class="mpf-banner mpf-banner&#45;&#45;success" role="region" aria-label="Success">-->
  <!--    <svg-->
  <!--      class="mpf-banner__icon"-->
  <!--      fill="currentColor"-->
  <!--      role="presentation"-->
  <!--      focusable="false"-->
  <!--      xmlns="http://www.w3.org/2000/svg"-->
  <!--      viewBox="0 0 25 25"-->
  <!--      height="25"-->
  <!--      width="25"-->
  <!--    >-->
  <!--      <path d="M25,6.2L8.7,23.2L0,14.1l4-4.2l4.7,4.9L21,2L25,6.2z" />-->
  <!--    </svg>-->
  <!--    <div class="mpf-banner__message">File named abc.jpg has been deleted</div>-->
  <!--  </div>-->
  <div :class="groupClasses">
    <MpfLabel
      :for-id="id"
      :label="label"
      :required="required"
      :size="size"
      :aria-describedby="hintId"
      :heading-level-size="headingLevelSize"
    />

    <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

    <div class="mpf-u-margin-top-4 mpf-u-margin-bottom-4">
      <div class="mpf-message mpf-message--warning mpf-message--with-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          class="mpf-icon"
          focusable="false"
          aria-hidden="true"
        >
          <path
            d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24l0 112c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-112c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"
          ></path>
        </svg>
        <div class="mpf-u-margin-left-2">
          <h2 class="mpf-heading-s mpf-u-margin-bottom-1">Warning</h2>
          <p class="mpf-u-margin-bottom-0">
            Do not upload photos of sensitive or intimate body parts, such as the groin, genitals,
            anus, bottom or breasts.
          </p>
        </div>
      </div>
    </div>

    <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
      {{ errors![0] }}
    </div>

    <div v-if="fileErrors" class="mpf-error-message" role="alert" aria-live="polite">
      {{ fileErrors }}
    </div>

    <input
      :id="id"
      type="file"
      multiple
      :accept="accept"
      class="mpf-file-upload mpf-u-margin-bottom-2"
      :aria-describedby="
        [hintId, errorId, fileErrors ? `${id}-file-error` : null].filter(Boolean).join(' ')
      "
      :disabled="files.length >= maxFiles"
      @change="onFileChange"
    />

    <p class="mpf-attachments__count">{{ files.length }} / {{ maxFiles }} files selected</p>
  </div>
</template>
<style lang="scss">
  .mpf-message {
    display: flex;
  }

  .mpf-icon {
    width: 2.5rem;
  }
</style>
